import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Bell,
  BookOpen,
  Building2,
  Calendar,
  CheckCircle2,
  CreditCard,
  DollarSign,
  Landmark,
  LogOut,
  Mail,
  Menu,
  Receipt,
  Shield,
  ShieldCheck,
  Sparkles,
  Trash2,
  User as UserIcon,
  Users,
  Workflow,
  XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Modal } from '@/components/Modal'
import { Sidebar } from './Sidebar'
import { menuItems } from './menuItems'
import { GuideChatWidget } from './GuideChatWidget'
import { useAuth } from '@/auth/useAuth'
import { api, ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { useProfile } from '@/features/profile/queries'

type NotificationItem = {
  id: number
  title: string
  created_at: string
  is_read: boolean
  type?: string | null
}

const CALENDLY_URL = 'https://calendly.com/bokapsys-support/new-meeting'

// Every icon button in the header shares these classes so the hit-area,
// radius, and icon size are visually identical regardless of background.
const HEADER_ICON_BTN = 'size-9 rounded-md [&_svg]:size-[18px]'

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

const EXTRA_TITLES: Record<string, string> = {
  '/platform-admin': 'Platform Admin',
  '/403': 'Access Denied',
  '/404': 'Page Not Found',
}

function titleForPath(pathname: string): string {
  if (EXTRA_TITLES[pathname]) return EXTRA_TITLES[pathname]
  const exact = menuItems.find((i) => i.path === pathname)
  if (exact) return exact.label
  const prefix = menuItems
    .filter((i) => pathname.startsWith(i.path + '/'))
    .sort((a, b) => b.path.length - a.path.length)[0]
  if (prefix) return prefix.label
  const seg = pathname.split('/').filter(Boolean).pop() || ''
  if (!seg) return 'Dashboard'
  return seg
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

type CalendlyApi = { initPopupWidget: (opts: { url: string }) => void }

function openCalendly() {
  const w = window as unknown as { Calendly?: CalendlyApi }
  const open = () => w.Calendly?.initPopupWidget({ url: CALENDLY_URL })
  if (w.Calendly?.initPopupWidget) {
    open()
    return
  }
  // Widget script is loaded async in index.html - poll briefly for it
  // before falling back to a new tab, so the popup works on a cold page.
  let waited = 0
  const id = window.setInterval(() => {
    waited += 100
    if (w.Calendly?.initPopupWidget) {
      window.clearInterval(id)
      open()
    } else if (waited >= 3000) {
      window.clearInterval(id)
      window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer')
    }
  }, 100)
}

export function Header() {
  const { user, logout, hasPermission } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [guideOpen, setGuideOpen] = useState(false)
  const profileQ = useProfile()
  const profileImageUrl = profileQ.data?.profile_image_url ?? null
  const [avatarBroken, setAvatarBroken] = useState(false)

  // Reset the broken flag when a new image comes back from the profile
  // query (e.g. after an upload or a fresh signed URL on refetch).
  useEffect(() => {
    setAvatarBroken(false)
  }, [profileImageUrl])

  // Calendly broadcasts `calendly.event_scheduled` when a booking completes -
  // we just surface a lightweight console confirmation; the webhook on the
  // BFF is the authoritative record.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (
        e.origin === 'https://calendly.com' &&
        typeof e.data === 'object' &&
        e.data &&
        'event' in e.data &&
        typeof (e.data as { event: string }).event === 'string' &&
        (e.data as { event: string }).event.startsWith('calendly.')
      ) {
        /* no-op - webhook handles persistence */
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const storedName =
    typeof window !== 'undefined' ? localStorage.getItem('userName') || '' : ''
  const displayName = storedName || user?.email?.split('@')[0] || 'User'
  const initials = displayName
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'

  const pageTitle = useMemo(() => titleForPath(location.pathname), [location.pathname])

  return (
    <>
      <header className="sticky top-0 z-30 flex h-16 items-center gap-3 bg-background/80 px-4 backdrop-blur sm:px-6 lg:px-10">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className={cn(HEADER_ICON_BTN, 'md:hidden')} aria-label="Open menu">
              <Menu />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <Sidebar />
          </SheetContent>
        </Sheet>

        <h1 className="min-w-0 truncate text-lg font-semibold tracking-tight text-foreground sm:text-xl">
          {pageTitle}
        </h1>

        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          {hasPermission('support.contact_sales') ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={openCalendly}
              aria-label="Schedule a meeting with our accounting team"
              title="Speak with our accounting team"
              className={cn(HEADER_ICON_BTN, 'bg-primary text-primary-foreground hover:bg-primary-hover hover:text-primary-foreground')}
            >
              <Calendar />
            </Button>
          ) : null}

          {hasPermission('ai.chatbot') ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setGuideOpen((v) => !v)}
              aria-label="AI Guide"
              title="Ask AI Guide"
              className={HEADER_ICON_BTN}
            >
              <Sparkles />
            </Button>
          ) : null}

          <ThemeToggle />

          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 gap-2 rounded-pill px-2 pr-3 text-sm font-medium"
                aria-label="Account menu"
              >
                {profileImageUrl && !avatarBroken ? (
                  <img
                    src={profileImageUrl}
                    alt={displayName}
                    onError={() => setAvatarBroken(true)}
                    className="size-7 rounded-pill object-cover ring-1 ring-border"
                  />
                ) : (
                  <span className="flex size-7 items-center justify-center rounded-pill bg-primary text-xs font-semibold text-primary-foreground">
                    {initials}
                  </span>
                )}
                <span className="hidden max-w-[160px] truncate sm:inline">{displayName}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="flex flex-col">
                <span className="text-sm font-medium">{displayName}</span>
                <span className="truncate text-xs font-normal text-muted-foreground">
                  {user?.email ?? '-'}
                </span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile">
                  <UserIcon className="size-4" />
                  <AccountMenuLabel label="Profile" location="Account" />
                </Link>
              </DropdownMenuItem>
              {hasPermission('security.manage') ? (
                <DropdownMenuItem asChild>
                  <Link to="/security">
                    <Shield className="size-4" />
                    <AccountMenuLabel label="Security (2FA)" location="Inside Profile" />
                  </Link>
                </DropdownMenuItem>
              ) : null}
              {hasPermission('tenant.settings.view') ? (
                <DropdownMenuItem asChild>
                  <Link to="/company">
                    <Building2 className="size-4" />
                    <AccountMenuLabel label="Company Settings" location="Account" />
                  </Link>
                </DropdownMenuItem>
              ) : null}
              {hasPermission('team.view') ? (
                <DropdownMenuItem asChild>
                  <Link to="/team-management">
                    <Users className="size-4" />
                    <AccountMenuLabel label="Team Management" location="Account" />
                  </Link>
                </DropdownMenuItem>
              ) : null}
              {hasPermission('billing.view') ? (
                <DropdownMenuItem asChild>
                  <Link to="/subscriptions">
                    <CreditCard className="size-4" />
                    <AccountMenuLabel label="Subscriptions" location="Sidebar footer" />
                  </Link>
                </DropdownMenuItem>
              ) : null}
              {hasPermission('support.contact_sales') ? (
                <DropdownMenuItem asChild>
                  <Link to="/contact-sales">
                    <Mail className="size-4" />
                    <AccountMenuLabel label="Contact us" location="Sidebar footer" />
                  </Link>
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem asChild>
                <Link to="/guide">
                  <BookOpen className="size-4" />
                  <AccountMenuLabel label="User Guide" location="Sidebar footer" />
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  await logout()
                  navigate('/login', { replace: true })
                }}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="size-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <GuideChatWidget open={guideOpen} onClose={() => setGuideOpen(false)} />
    </>
  )
}

function AccountMenuLabel({ label, location }: { label: string; location: string }) {
  return (
    <span className="flex min-w-0 flex-1 flex-col leading-tight">
      <span className="truncate">{label}</span>
      <span className="truncate text-[11px] font-normal text-muted-foreground">{location}</span>
    </span>
  )
}

type NotificationFilter = 'unread' | 'all'

// Phase 3c - notification icon mapping. Falls back to a colored dot
// for unknown types so future notification kinds render gracefully
// before this map is updated.
function notificationIcon(type: string | undefined) {
  switch (type) {
    case 'invoice_paid':         return <DollarSign className="size-4 text-success" />
    case 'approval_requested':   return <ShieldCheck className="size-4 text-warning" />
    case 'approval_approved':    return <CheckCircle2 className="size-4 text-success" />
    case 'approval_rejected':    return <XCircle className="size-4 text-destructive" />
    case 'budget_alert':         return <DollarSign className="size-4 text-warning" />
    case 'workflow':             return <Workflow className="size-4 text-primary" />
    case 'plaid_review':         return <Landmark className="size-4 text-primary" />
    case 'bill_received':        return <Receipt className="size-4 text-primary" />
    case 'payment_failed':       return <XCircle className="size-4 text-destructive" />
    case 'anomaly_detected':     return <Sparkles className="size-4 text-warning" />
    default:                     return null
  }
}

function NotificationBell() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const tenantId = user?.tenantId ?? null
  const [count, setCount] = useState<number>(0)
  const [items, setItems] = useState<NotificationItem[]>([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState<NotificationFilter>('unread')
  const [open, setOpen] = useState(false)
  const [confirmClearOpen, setConfirmClearOpen] = useState(false)
  const [clearing, setClearing] = useState(false)

  // Blow away local state when the active tenant changes so the bell
  // never shows a count or list from the previous workspace - even
  // briefly - while the fresh fetch is in flight.
  useEffect(() => {
    setCount(0)
    setItems([])
    setOpen(false)
    setFilter('unread')
  }, [tenantId])

  useEffect(() => {
    // Don't poll before we have an active tenant (e.g. during logout).
    if (!tenantId) return
    let cancelled = false
    const fetchCount = async () => {
      try {
        const data = await api.get<{ count: number }>('/api/notifications/count')
        if (!cancelled) setCount(data.count ?? 0)
      } catch {
        /* silent */
      }
    }
    void fetchCount()
    const id = window.setInterval(() => void fetchCount(), 30_000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
    // Re-subscribing when tenantId flips guarantees the polling loop
    // attached to the previous tenant's bearer is torn down.
  }, [tenantId])

  const loadList = async (mode: NotificationFilter) => {
    setLoading(true)
    try {
      const data = await api.get<{ notifications?: NotificationItem[] }>(
        `/api/notifications?filter=${mode}`,
      )
      setItems(data.notifications ?? [])
    } catch (err) {
      if (err instanceof ApiError) setItems([])
    } finally {
      setLoading(false)
    }
  }

  // Reload when the menu opens, the filter changes, or the tenant switches.
  useEffect(() => {
    if (open && tenantId) void loadList(filter)
  }, [open, filter, tenantId])

  const onItemClick = async (n: NotificationItem) => {
    if (!n.is_read) {
      try {
        await api.post(`/api/notifications/${n.id}/read`)
        setCount((c) => Math.max(0, c - 1))
        // Reflect the read state in the list so "All" view stays consistent
        // and "Unread" view drops the item without a round-trip.
        setItems((prev) =>
          filter === 'unread'
            ? prev.filter((it) => it.id !== n.id)
            : prev.map((it) => (it.id === n.id ? { ...it, is_read: true } : it)),
        )
      } catch {
        /* silent */
      }
    }
    setOpen(false)
    // Resource-aware deep-link routing. Falls back to the legacy Plaid
    // route only for plaid_review notifications; everything else goes
    // to the surface that owns the resource.
    if (n.type === 'budget_alert') navigate('/budget-planning')
    else if (n.type === 'workflow') navigate('/workflows')
    else if (n.type === 'invoice_paid') {
      const inv = (n as unknown as { invoice_id?: number }).invoice_id
      navigate(inv ? `/invoicing/invoices/${inv}` : '/invoicing/invoices')
    }
    else if (n.type === 'approval_requested' || n.type === 'approval_approved' || n.type === 'approval_rejected') {
      const inst = (n as unknown as { approval_instance_id?: number }).approval_instance_id
      navigate(inst ? `/approvals/instances/${inst}` : '/approvals')
    }
    else if (n.type === 'plaid_review') navigate(`/plaid?notificationId=${n.id}`)
    else navigate('/dashboard')
  }

  const onClearAll = async () => {
    setClearing(true)
    try {
      await api.del('/api/notifications')
      setItems([])
      setCount(0)
      setConfirmClearOpen(false)
    } catch {
      /* silent */
    } finally {
      setClearing(false)
    }
  }

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(HEADER_ICON_BTN, 'relative')}
            aria-label="Notifications"
          >
            <Bell />
            {count > 0 ? (
              <span
                aria-label={`${count} unread`}
                className="absolute right-0.5 top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground ring-2 ring-card"
              >
                {count > 99 ? '99+' : count}
              </span>
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 p-0">
          <div className="flex items-center justify-between px-3 py-2">
            <DropdownMenuLabel className="p-0 text-sm font-semibold">
              Notifications
            </DropdownMenuLabel>
            {items.length > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-destructive"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setConfirmClearOpen(true)
                }}
                aria-label="Clear all notifications"
              >
                <Trash2 className="size-3.5" />
                Clear all
              </Button>
            ) : null}
          </div>
          <div
            role="tablist"
            aria-label="Notification filter"
            className="flex gap-1 border-b px-3 pb-2"
          >
            <FilterTab
              active={filter === 'unread'}
              onClick={() => setFilter('unread')}
              label="Unread"
              count={count}
            />
            <FilterTab
              active={filter === 'all'}
              onClick={() => setFilter('all')}
              label="All"
            />
          </div>
          <div className="max-h-80 overflow-auto">
            {loading ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications'}
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => void onItemClick(n)}
                  className={cn(
                    'flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent',
                    !n.is_read && 'bg-primary/5',
                  )}
                >
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center">
                    {notificationIcon(n.type ?? undefined) ?? (
                      <span className={cn('size-2 rounded-full', n.is_read ? 'bg-transparent' : 'bg-primary')} />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block truncate',
                        n.is_read ? 'font-normal text-muted-foreground' : 'font-medium',
                      )}
                    >
                      {n.title}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {timeAgo(n.created_at)}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <Modal.Confirm
        open={confirmClearOpen}
        onOpenChange={setConfirmClearOpen}
        title="Clear all notifications?"
        body="This removes every notification from your inbox. It does not affect the underlying workflow, budget, or Plaid data."
        confirmLabel={clearing ? 'Clearing…' : 'Clear all'}
        confirmVariant="danger"
        onConfirm={onClearAll}
        busy={clearing}
      />
    </>
  )
}

function FilterTab({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  label: string
  count?: number
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onClick()
      }}
      className={cn(
        'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
        active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent',
      )}
    >
      {label}
      {typeof count === 'number' && count > 0 ? (
        <span
          className={cn(
            'ml-1 rounded-full px-1.5 text-[10px] font-semibold',
            active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
          )}
        >
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </button>
  )
}
